import { createContext, useContext, useCallback, useEffect, useState } from "react";
import { getSettings } from "../lib/api";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

export const computeShipping = (subtotal, cfg) => {
  if (!cfg || cfg.enabled === false) return 0;
  const freeOver = Number(cfg.free_over) || 0;
  if (freeOver > 0 && subtotal >= freeOver) return 0;
  const tiers = [...(cfg.tiers || [])].sort((a, b) => (a.up_to || 0) - (b.up_to || 0));
  for (const t of tiers) {
    if (subtotal <= (Number(t.up_to) || 0)) return Number(t.cost) || 0;
  }
  return Number(cfg.default_cost) || 0;
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("cart") || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);
  const [cartEnabled, setCartEnabled] = useState(true);
  const [shipping, setShipping] = useState(null);
  const [promo, setPromo] = useState(null);
  const [minOrderQty, setMinOrderQty] = useState(12);

  useEffect(() => {
    localStorage.setItem("cart", JSON.stringify(items));
  }, [items]);

  const refreshSettings = useCallback(() => {
    return getSettings()
      .then((s) => {
        setCartEnabled(s.cart_enabled);
        setShipping(s.shipping || null);
        setPromo(s.promo_banner || null);
        setMinOrderQty(Number(s.min_order_qty) || 12);
        return s;
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  const addItem = (product) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product.id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, quantity: i.quantity + 1 } : i
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
    setOpen(true);
  };

  const updateQty = (id, delta) => {
    setItems((prev) =>
      prev
        .map((i) =>
          i.product.id === id ? { ...i, quantity: i.quantity + delta } : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (id) =>
    setItems((prev) => prev.filter((i) => i.product.id !== id));
  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const total = items.reduce((s, i) => s + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, updateQty, removeItem, clear, count, total, open, setOpen, cartEnabled, setCartEnabled, shipping, promo, minOrderQty, refreshSettings }}
    >
      {children}
    </CartContext.Provider>
  );
};
